// Reset Password page — landing target for the email link emitted by
// auth.forgotPassword. Reads ?token=xxx from the URL, accepts a new password,
// calls auth.resetPassword, then bounces back to /login with a success flag.

import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import { Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { STANDARD_LOGO_SRC } from '@/lib/brandAssets';
import '@/styles/light-entry.css';

const BG_URL = 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80';

export default function ResetPassword() {
  const [, navigate] = useLocation();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') ?? '', []);
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const reset = trpc.auth.resetPassword.useMutation({
    onSuccess: () => {
      toast.success('Password updated. Sign in with your new password.');
      navigate('/login');
    },
    onError: (e) => setErrorMsg(e.message || 'Could not reset password.'),
  });

  useEffect(() => {
    if (!token) setErrorMsg('Reset link is missing a token. Request a new one from the login page.');
  }, [token]);

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMsg('');
    if (!token) return;
    if (next.length < 8) { setErrorMsg('Use at least 8 characters.'); return; }
    if (next !== confirm) { setErrorMsg('Passwords do not match.'); return; }
    reset.mutate({ token, newPassword: next });
  };

  return (
    <div className="le-shell le-center">
      <div className="le-frame le-stack">
        <div className="le-brand-plate" data-photo="" style={{ backgroundImage: `url(${BG_URL})` }}>
          <img src={STANDARD_LOGO_SRC} alt="The Standard" className="le-brand-logo" />
        </div>

        <div className="le-card le-card-pad">
          <div className="flex items-center justify-center gap-2">
            <KeyRound className="w-4 h-4" style={{ color: '#7d5f2c' }} strokeWidth={1.75} />
            <p className="le-eyebrow">
              Account Recovery
            </p>
          </div>
          <h1 className="le-title le-title-sm le-center-text mt-2">Set a new password</h1>
          <p className="le-copy le-center-text mt-2 mb-5">
            Choose a new password to sign back in.
          </p>

          {errorMsg && (
            <div role="alert" className="le-alert le-alert-error mb-4">
              {errorMsg}
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="New password (min 8 characters)"
              aria-label="New password"
              autoComplete="new-password"
              autoFocus
              required
              disabled={!token || reset.isPending}
              className="le-input"
            />
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              aria-label="Confirm new password"
              autoComplete="new-password"
              required
              disabled={!token || reset.isPending}
              className="le-input"
            />
            <button
              type="submit"
              disabled={!token || reset.isPending}
              className="le-btn le-btn-primary"
            >
              {reset.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              {reset.isPending ? 'Updating...' : 'Set New Password'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="le-text-btn le-text-btn-block"
            >
              Back to login
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
